/**
 * <ll-bundle-builder> — interactivity for sections/ll-bundle-builder.liquid.
 *
 * Reads the JSON config embedded by the section ([data-llb-config]) and manages
 * per-slot color/size selection. The add-to-cart form is the theme's native
 * `product-form` custom element, so this script never talks to /cart/add
 * itself — it only:
 *   1. keeps the slot cards (swatches, size pills, thumbnail, titles) in sync,
 *   2. fills + enables the hidden `_bundleId` / `_compiledIds` /
 *      `_compiledItems` property inputs once every slot has a size,
 *   3. toggles the submit button and the "Finish size selection" hint.
 *
 * Loaded only on the product.bundle template (see layout/theme.liquid).
 */
(() => {
  if (window.customElements.get('ll-bundle-builder')) return;

  const esc = (value) =>
    String(value == null ? '' : value).replace(/[&<>"']/g, (ch) => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
    }[ch]));

  class LLBundleBuilder extends HTMLElement {
    connectedCallback() {
      const configEl = this.querySelector('[data-llb-config]');
      if (!configEl) return;

      try {
        this.config = JSON.parse(configEl.textContent);
      } catch (error) {
        console.warn('[ll-bundle] invalid config JSON', error);
        return;
      }

      this.form = document.getElementById(this.dataset.formId);
      this.hint = this.querySelector('[data-llb-hint]');
      this.submitButton = this.form ? this.form.querySelector('button[type="submit"]') : null;
      this.propInputs = {};
      ['bundleId', 'compiledIds', 'compiledItems'].forEach((key) => {
        this.propInputs[key] = this.form ? this.form.querySelector(`[data-llb-prop="${key}"]`) : null;
      });

      this.state = this.config.slots.map((slot) => {
        let colorIdx = slot.colors.findIndex((c) => c.handle === slot.defaultHandle);
        if (colorIdx < 0) colorIdx = 0;
        return { colorIdx, variantId: null };
      });

      this.slotEls = Array.from(this.querySelectorAll('.llb-slot'));
      this.slotEls.forEach((_, index) => this.renderSlot(index));
      // Delegate from the section root: the media gallery thumbs live in the
      // sibling column, outside this element.
      (this.closest('.llb') || this).addEventListener('click', this.onClick.bind(this));
      this.sync();
    }

    onClick(event) {
      const swatch = event.target.closest('[data-color-idx]');
      if (swatch) {
        this.selectColor(Number(swatch.dataset.slot), Number(swatch.dataset.colorIdx));
        return;
      }

      const size = event.target.closest('[data-variant-id]');
      if (size && !size.disabled) {
        this.state[Number(size.dataset.slot)].variantId = Number(size.dataset.variantId);
        this.renderSlot(Number(size.dataset.slot));
        this.sync();
        return;
      }

      const thumb = event.target.closest('[data-thumb-index]');
      if (thumb) this.selectGalleryImage(Number(thumb.dataset.thumbIndex));
    }

    selectColor(slotIndex, colorIdx) {
      const state = this.state[slotIndex];
      if (state.colorIdx === colorIdx) return;

      const previousColor = this.config.slots[slotIndex].colors[state.colorIdx];
      const nextColor = this.config.slots[slotIndex].colors[colorIdx];
      state.colorIdx = colorIdx;

      // Keep the chosen size across a color switch when the same size exists
      // and is in stock on the new color; otherwise the size must be re-picked.
      if (state.variantId) {
        const previousSize = previousColor.sizes.find((s) => s.variantId === state.variantId);
        const match = previousSize
          ? nextColor.sizes.find((s) => s.label === previousSize.label && s.available)
          : null;
        state.variantId = match ? match.variantId : null;
      }

      this.renderSlot(slotIndex);
      this.sync();
    }

    renderSlot(slotIndex) {
      const slotEl = this.slotEls[slotIndex];
      const slot = this.config.slots[slotIndex];
      const state = this.state[slotIndex];
      const color = slot.colors[state.colorIdx];
      if (!slotEl || !color) return;

      const thumbImg = slotEl.querySelector('[data-slot-thumb] img');
      if (thumbImg && color.image) {
        thumbImg.src = color.image;
        thumbImg.removeAttribute('srcset');
        thumbImg.alt = color.title;
      }

      const titleEl = slotEl.querySelector('[data-slot-product-title]');
      if (titleEl) titleEl.textContent = color.shortTitle;

      const colorNameEl = slotEl.querySelector('[data-slot-color-name]');
      if (colorNameEl) colorNameEl.textContent = color.colorName || '';

      const colorsBlock = slotEl.querySelector('.llb-slot__colors');
      const singleUnnamedColor = slot.colors.length === 1 && !slot.colors[0].colorName;
      if (colorsBlock) colorsBlock.hidden = singleUnnamedColor;

      const swatchList = slotEl.querySelector('[data-slot-swatches]');
      if (swatchList) {
        swatchList.innerHTML = slot.colors
          .map((c, idx) => {
            const selected = idx === state.colorIdx;
            const style = c.swatchUrl
              ? `background-image:url('${esc(c.swatchUrl)}')`
              : `background-color:${esc(c.colorValue) || '#e5e0da'}`;
            return `<li><button type="button" class="llb-swatch${selected ? ' is-selected' : ''}" data-slot="${slotIndex}" data-color-idx="${idx}" style="${style}" aria-label="${esc(c.colorName)}" aria-pressed="${selected}" title="${esc(c.colorName)}"></button></li>`;
          })
          .join('');
      }

      const sizesEl = slotEl.querySelector('[data-slot-sizes]');
      if (sizesEl) {
        sizesEl.innerHTML = color.sizes
          .map((size) => {
            const selected = size.variantId === state.variantId;
            return `<button type="button" class="llb-size${selected ? ' is-selected' : ''}" data-slot="${slotIndex}" data-variant-id="${size.variantId}" aria-pressed="${selected}" ${size.available ? '' : 'disabled'}>${esc(size.label)}</button>`;
          })
          .join('');
      }
    }

    sync() {
      const complete = this.state.every((s) => s.variantId);
      const started = this.state.some((s) => s.variantId);

      if (complete) {
        const chosen = this.state.map((state, index) => {
          const color = this.config.slots[index].colors[state.colorIdx];
          const size = color.sizes.find((s) => s.variantId === state.variantId);
          return { color, size };
        });

        this.setProp('compiledIds', chosen.map((c) => `gid://shopify/ProductVariant/${c.size.variantId}`).join(', '));
        this.setProp('compiledItems', chosen.map((c) => `${c.color.title}|${c.size.label}`).join(', '));
        this.setProp('bundleId', `id_${Date.now()}_${Math.random().toString(36).slice(2, 11)}_${this.config.shell.productId}`);
      } else {
        Object.keys(this.propInputs).forEach((key) => {
          const input = this.propInputs[key];
          if (input) {
            input.value = '';
            input.disabled = true;
          }
        });
      }

      if (this.submitButton) this.submitButton.disabled = !complete;
      if (this.hint) this.hint.hidden = complete || !started;
    }

    setProp(key, value) {
      const input = this.propInputs[key];
      if (!input) return;
      input.value = value;
      input.disabled = false;
    }

    selectGalleryImage(index) {
      const images = Array.from(this.closest('.llb')?.querySelectorAll('.llb-media__image') || []);
      const thumbs = Array.from(this.closest('.llb')?.querySelectorAll('[data-thumb-index]') || []);
      images.forEach((img, i) => img.classList.toggle('is-active', i === index));
      thumbs.forEach((thumb, i) => thumb.classList.toggle('is-selected', i === index));
    }
  }

  window.customElements.define('ll-bundle-builder', LLBundleBuilder);
})();
